/**
 * SessionSummary.jsx — Résumé session en langage naturel
 *
 * Charge les templates depuis l'API, génère le résumé depuis les stats + readings,
 * affiche un paragraphe interprétatif destiné au vétérinaire et au propriétaire.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../utils/authFetch';
import { generateSummary } from './sessionSummaryGenerator';
import defaultTemplates from '../constants/sessionSummaryTemplates.json';
import summaryTemplatesEn from '../constants/sessionSummaryTemplates.en.json';
import { getTheme, TYPE, RADIUS } from '../constants/theme';
import { classifyPm } from '../constants/classifyPm';

// scope 'period' : formulations « période » (bloc period des modèles), sur la
// session fenêtrée et les mesures de la période (periodAnalysis).
// providedTemplates : modèles FR déjà chargés par la page du lien de séance
// (route publique sous jeton, D247) ; la route authentifiée n'est alors pas appelée.
const SessionSummary = ({ session, readings = [], darkMode = false, scope = 'session', templates: providedTemplates = null }) => {
  const { t: tr, i18n } = useTranslation(); // `t` ci-dessous = thème (getTheme) → traduction aliasée `tr`
  const isEN = String(i18n.language || 'fr').startsWith('en');
  const [templates, setTemplates] = useState(defaultTemplates);
  const [loading, setLoading]     = useState(true);
  const t = getTheme(darkMode);

  // EN : templates front-only (sessionSummaryTemplates.en.json), comme PDFExport
  // (i18n 1.4b-3 Option C) — le backend n'a pas de notion de langue, donc pas de
  // fetch en EN. FR : flux backend INCHANGÉ (override super_admin honoré, défaut
  // front si API non joignable).
  useEffect(() => {
    if (isEN || providedTemplates) { setLoading(false); return; }
    fetch('/api/sessions/summary-templates', {
      credentials: 'include',
      headers: getAuthHeaders(),
    })
      .then(r => r.json())
      .then(data => { if (data.success) setTemplates(data.templates); })
      .catch(() => { /* garder les défauts */ })
      .finally(() => setLoading(false));
  }, [isEN, providedTemplates]);

  if (!session || loading) return null;

  const activeTemplates = isEN ? summaryTemplatesEn : (providedTemplates || templates);
  const sentences = generateSummary(session, readings, activeTemplates, { scope });
  if (!sentences.length) return null;

  // Couleur de l'accent selon le niveau global (pm2_5_avg), paliers classifyPm
  // (mêmes bornes que les seuils écrits ici en dur avant 10.5.313).
  const accentColor = {
    bon: t.success, a_surveiller: t.warning, eleve: t.danger, non_mesure: t.textSecondary,
  }[classifyPm(session.pm2_5_avg, 'pm25').level];

  return (
    <div data-testid="session-summary" style={{
      background: darkMode ? 'rgba(27,119,241,0.06)' : '#F0F7FF',
      border: `1px solid ${darkMode ? 'rgba(27,119,241,0.2)' : '#BFDBFE'}`,
      borderLeft: `4px solid ${accentColor}`,
      borderRadius: RADIUS.lg,
      padding: '18px 20px',
      marginBottom: '16px',
    }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={accentColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
        <span style={{ ...TYPE.label, fontSize: '13px', fontWeight: '700', color: accentColor }}>
          {tr('session.summary.title')}
        </span>
        <span style={{
          fontSize: '10px', color: t.textSecondary, marginLeft: 'auto',
          fontStyle: 'italic', fontFamily: TYPE.fontBody,
        }}>
          {tr('session.summary.toValidate')}
        </span>
      </div>

      {/* Phrases */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {sentences.map((sentence, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{
              width: '5px', height: '5px', borderRadius: '50%',
              background: accentColor, flexShrink: 0, marginTop: '7px',
            }} />
            <p style={{
              ...TYPE.body, color: t.textPrimary, margin: 0, lineHeight: '1.7',
            }}>
              {sentence}
            </p>
          </div>
        ))}
      </div>

      {/* Note de bas de page */}
      <p style={{
        ...TYPE.small, color: t.textSecondary, margin: '12px 0 0',
        fontStyle: 'italic', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#DBEAFE'}`,
        paddingTop: '10px',
      }}>
        {tr('session.summary.note')}
      </p>
    </div>
  );
};

export default SessionSummary;
